'use client'

import { useState, MouseEvent } from 'react'
import { Button, Cell, Checkbox, Dialog, Spacer, Text, Icon } from '@vinted/web-ui'
import { Settings24 } from '@vinted/monochrome-icons'

import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { List } from '@marketplace-web/common-components/list-ui'
import { generateRandomUser } from '@marketplace-web/registration/identity-data'
import { useIncogniaTracking } from '@marketplace-web/bot-detection/incognia-feature'

import { ROOT_URL } from '../../constants/routes'

const RandomUserSignup = () => {
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [isGod, setIsGod] = useState(true)

  const { getIncogniaRequestHeaders } = useIncogniaTracking()

  const handleToggleModal = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    setIsSettingsModalOpen(prevState => !prevState)
  }

  const handleGenerateUser = async (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    setHasError(false)
    setIsLoading(true)

    try {
      await generateRandomUser({ isGod }, { headers: await getIncogniaRequestHeaders() })

      navigateToPage(ROOT_URL)
    } catch (exception) {
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleIsGod = () => {
    setIsGod(prevState => !prevState)
  }

  const renderError = () => {
    if (!hasError) return null

    return (
      <>
        <Text
          as="h3"
          theme="warning"
          type={Text.Type.Subtitle}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
          text="Server error"
        />
        <Spacer />
      </>
    )
  }

  const renderModal = () => (
    <Dialog show={isSettingsModalOpen}>
      <List>
        <Cell styling={Cell.Styling.Wide}>
          <Text
            as="h2"
            type={Text.Type.Title}
            alignment={Text.Alignment.Center}
            width={Text.Width.Parent}
            text="Generate user settings"
          />
        </Cell>
        <Cell
          styling={Cell.Styling.Wide}
          title="Assign god role?"
          suffix={
            <Checkbox
              name="is_god"
              checked={isGod}
              onClick={handleToggleIsGod}
              onChange={() => undefined}
            />
          }
        />
        <Cell styling={Cell.Styling.Wide}>
          <Button
            text="Save"
            styling={Button.Styling.Filled}
            isLoading={isLoading}
            onClick={handleToggleModal}
          />
        </Cell>
      </List>
    </Dialog>
  )

  return (
    <>
      {renderError()}
      <div className="u-flexbox">
        <Button text="Generate random user" isLoading={isLoading} onClick={handleGenerateUser} />
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <Button
          icon={<Icon name={Settings24} color={Icon.Color.Primary} />}
          inline
          onClick={handleToggleModal}
        />
      </div>
      {renderModal()}
    </>
  )
}

export default RandomUserSignup
